import { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import CouponDialog from '../../../../components/CouponDialog/CouponDialog';
import surrogateId from '../../../../helpers/string/surrogateId';
import useCurrentUrl from '../../../../hooks/inertia/useCurrentUrl';
import useDismissableProp from '../../../../hooks/react/useDismissableProp';
import useBeforeUnload from '../../../../hooks/ui/useBeforeUnload';
import useTrans from '../../../../hooks/ui/useTrans';
import useOverlayStatus from '../../../components/OverlayStatusContext/useOverlayStatus';
import PaymentErrorDialog from '../../../components/PaymentErrorDialog/PaymentErrorDialog';
import SidebarAndMain from '../../../components/SidebarAndMain/SidebarAndMain';
import AutoOverlayOrDefaultLayout from '../../../layouts/AutoOverlayOrDefaultLayout';
import CouponOverlayHeaderButton from '../components/CouponOverlayHeaderButton/CouponOverlayHeaderButton';
import CustomerBookingConditionDialog from '../components/CustomerBookingConditionDialog/CustomerBookingConditionDialog';
import useBookingFormProps from '../hooks/useBookingFormProps';
import useComposeBookingProps from '../hooks/useComposeBookingProps';

import loginFormDisplayFromUIParameters from './helpers/loginFormDisplayFromUIParameters';
import useDatePickerModeAtLoad from './hooks/useDatePickerModeAtLoad';

type BookingFormLayoutProps = {
    titleTranslationKey: string;
    children: ReactNode;
    sidebar?: ReactNode;
    allowLeaveWithoutWarning?: boolean;
    preventLoginOrLogout?: boolean;
};

const BookingFormLayout = ({
    titleTranslationKey,
    children,
    sidebar,
    allowLeaveWithoutWarning = false,
    preventLoginOrLogout = false,
}: BookingFormLayoutProps) => {
    const intl = useIntl();
    const trans = useTrans('CustomerArea.BookingForm.Shared.');

    const title = intl.formatMessage({ id: titleTranslationKey });

    const {
        tenant,
        inlineLoginUrl,
        inlineRequestPasswordResetUrl,
        uiParameters,
        datePickerMode,
        appliedCouponCode,
        validateCouponCodeUrl,
        applyCouponCodeUrl,
        paymentError,
        errors,
    } = useBookingFormProps();
    const currentUrl = useCurrentUrl();
    const { overlayIsClosing } = useOverlayStatus();
    const dismissibleCouponCodeError = useDismissableProp({
        prop: errors?.couponCode,
    });
    const dismissibleCustomerTypeError = useDismissableProp(
        { prop: errors?.customerTypeId },
        [errors]
    );

    /**
     * Set the date picker mode at load to the passed prop and remember for as
     * long as this layout is mounted.
     */
    useDatePickerModeAtLoad(datePickerMode);

    const { parameters } = useComposeBookingProps();
    const [showCouponDialog, setShowCouponDialog] = useState(
        uiParameters?.showCouponForm ?? false
    );

    useEffect(() => {
        if (errors?.couponCode) {
            setShowCouponDialog(true);
        }
    }, [errors?.couponCode]);

    useBeforeUnload({
        message: trans('BeforeLeaveMessage'),
        enabled: !allowLeaveWithoutWarning && !overlayIsClosing,
    });

    return (
        <AutoOverlayOrDefaultLayout
            title={title}
            inlineLoginUrl={inlineLoginUrl}
            inlineRequestPasswordResetUrl={inlineRequestPasswordResetUrl}
            headerOptions={{
                inlineLogin: true,
                alignWithBookingForm: true,
                minimalOnMobile: true,
                hideLanguageSwitch: uiParameters?.hideLanguageSwitch,
                preventLoginOrLogout: preventLoginOrLogout,
                defaultLoginFormDisplay:
                    loginFormDisplayFromUIParameters(uiParameters),
                wrapper: (children) => (
                    <SidebarAndMain
                        // Compensate for the sidebar in the main region
                        sidebar={!!sidebar && <span />}
                    >
                        {children}
                    </SidebarAndMain>
                ),
            }}
            headerButtons={
                <CouponOverlayHeaderButton
                    hasCouponApplied={!!appliedCouponCode}
                    onClick={() => setShowCouponDialog(true)}
                />
            }
            width="wide"
        >
            <SidebarAndMain sidebar={sidebar}>{children}</SidebarAndMain>

            <CouponDialog
                visible={showCouponDialog}
                onClose={() => setShowCouponDialog(false)}
                mode="customer-area"
                appliedCouponCode={appliedCouponCode}
                errors={dismissibleCouponCodeError.value}
                currentUrl={currentUrl}
                applyCouponCodeUrl={applyCouponCodeUrl}
                validateCouponCodeUrl={validateCouponCodeUrl}
                validationParameters={parameters}
                currencyCode={tenant.currencyCode}
            />

            {dismissibleCustomerTypeError.value && (
                <CustomerBookingConditionDialog
                    onBack={dismissibleCustomerTypeError.dismiss}
                    error={dismissibleCustomerTypeError.value}
                />
            )}

            <PaymentErrorDialog
                paymentError={paymentError}
                key={surrogateId()}
            />
        </AutoOverlayOrDefaultLayout>
    );
};

export default BookingFormLayout;
